import React, { useMemo } from "react";
import { Box, Grid, Paper, Typography, Stack } from "@mui/material";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import OpacityOutlinedIcon from "@mui/icons-material/OpacityOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import { useSelector } from "react-redux";
import { theme } from "../../../Theme";
import { PROMO_COLORS } from "../constants/colors";
import { resolveBrandSummaryMetrics } from "../utils/brandSummaryMetrics";

function BrandMetricCard({ label, value, accent, icon }) {
  const tint = accent || theme.palette.primary.main;
  return (
    <Box
      sx={{
        p: 2.25,
        borderRadius: 2,
        backgroundColor: "#fff",
        border: "1px solid #E8ECF0",
        height: "100%",
        position: "relative",
        overflow: "hidden",
        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          boxShadow: "0 4px 16px rgba(15, 23, 42, 0.07)",
          borderColor: `${tint}40`,
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${tint} 0%, ${tint}66 100%)`,
          opacity: 0.85,
        },
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: 11, fontWeight: 600, display: "block", mb: 0.25, pr: icon ? 1 : 0 }}
        >
          {label}
        </Typography>
        {icon && (
          <Box
            sx={{
              flexShrink: 0,
              width: 34,
              height: 34,
              borderRadius: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: `${tint}12`,
              color: tint,
            }}
          >
            {icon}
          </Box>
        )}
      </Stack>
      <Typography
        variant="h5"
        fontWeight={700}
        sx={{
          mt: 1,
          fontSize: { xs: "1.25rem", sm: "1.35rem" },
          color: accent || theme.palette.primary.main,
          letterSpacing: "-0.02em",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function fmtFixed(val, decimals = 2) {
  if (val === undefined || val === null || Number.isNaN(Number(val))) {
    return "—";
  }
  return Number(val).toFixed(decimals);
}

function BrandMetricsGrid({ agg }) {
  const a = agg || {};
  return (
    <Grid container spacing={2.25}>
      <Grid item xs={6} md={3}>
        <BrandMetricCard
          label="Total volume (L)"
          value={fmtFixed(a.totalVolume)}
          icon={<OpacityOutlinedIcon sx={{ fontSize: 20 }} />}
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <BrandMetricCard
          label="Total contribution margin"
          value={
            a.totalContributionMargin == null ? "—" : fmtFixed(a.totalContributionMargin)
          }
          accent={PROMO_COLORS.safe}
          icon={<PaymentsOutlinedIcon sx={{ fontSize: 20 }} />}
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <BrandMetricCard
          label="ROI"
          value={a.roi == null ? "—" : `${fmtFixed(a.roi)}×`}
          icon={<TrendingUpOutlinedIcon sx={{ fontSize: 20 }} />}
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <BrandMetricCard
          label="Total discount spend"
          value={fmtFixed(a.totalDiscountSpend)}
          icon={<PercentOutlinedIcon sx={{ fontSize: 20 }} />}
        />
      </Grid>
    </Grid>
  );
}

export default function BrandSummaryStrip({ sx: paperSx, scenarios: scenariosProp }) {
  const scenariosRedux = useSelector((state) => state.promoSimulator.scenarios);
  const scenarios = scenariosProp ?? scenariosRedux;

  const brandSummary = useMemo(
    () =>
      resolveBrandSummaryMetrics({
        scenarios,
        brandLabel: null,
        weekStart: null,
        weekEnd: null,
      }),
    [scenarios]
  );

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        borderRadius: 2,
        border: "1px solid #E8ECF0",
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
        ...paperSx,
      }}
    >
      <Box
        sx={{
          px: { xs: 2.25, sm: 3 },
          py: { xs: 2.25, sm: 2.75 },
          backgroundColor: "#fff",
          borderLeft: `4px solid ${theme.palette.primary.main}`,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.75}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          mb={2.25}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <InsightsOutlinedIcon sx={{ color: theme.palette.primary.main, opacity: 0.9 }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color={theme.palette.primary.main}>
                Brand summary
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                These numbers come only from saved scenarios and refresh automatically when you add or
                remove one.
              </Typography>
            </Box>
          </Stack>
        </Stack>

        <BrandMetricsGrid agg={brandSummary} />
      </Box>
    </Paper>
  );
}
